import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { useMutation, useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";

import { apiEndpoints } from "@admin/constants/api-endpoints";
import { LoginInput } from "@backend/schemas/auth.schema";

import { apiClient, setGlobalRefreshFunction } from "@admin/utils/api-helpers";
import { authController } from "@admin/utils/auth-controller";
import { navigateToLogin, getReturnPath } from "@admin/utils/auth-navigation";

// Update the Auth API Response interface to match the actual API response structure
interface AuthApiResponse {
	success: boolean;
	data: {
		user: {
			id: number;
			email: string;
			firstName?: string;
			surname?: string;
			role: string;
		};
	};
	timestamp: string;
}

export const useAuth = () => {
	const navigate = useNavigate();

	// React state that syncs with controller state
	const [state, setState] = useState(authController.getState());

	// Subscribe to auth controller changes
	useEffect(() => {
		return authController.subscribe(() => {
			setState(authController.getState());
		});
	}, []);

	// Set up the global refresh function
	const refresh = useCallback(async () => {
		try {
			console.log("Attempting to refresh authentication token");

			if (!authController.canRefreshToken()) {
				throw new Error("Token refresh already in progress");
			}

			authController.setRefreshing(true);

			const response = await apiClient.post<AuthApiResponse>(
				apiEndpoints.auth.refresh,
				{},
				"Token refresh failed"
			);

			// Fix the data access path
			const userData = response?.data?.user;

			// Update auth state with refreshed data
			if (userData) {
				console.log("Token refreshed successfully");
				authController.setAuthenticated(true, userData);
				return response;
			} else {
				console.error("Unexpected response structure from refresh:", response);
				throw new Error("Invalid response format from token refresh");
			}
		} catch (error) {
			// Handle refresh errors here
			console.error("Token refresh failed:", error);
			authController.resetAuth();
			throw error;
		} finally {
			authController.setRefreshing(false);
		}
	}, []);

	// Set up the global refresh function when the component mounts
	useEffect(() => {
		setGlobalRefreshFunction(refresh);
	}, [refresh]);

	// Initial auth check query
	const { refetch: refetchAuth } = useQuery({
		queryKey: ["authCheck"],
		queryFn: async () => {
			// Only check if we're not already authenticated
			if (!authController.canCheckAuth()) {
				console.log("Auth check throttled or in progress");
				return null;
			}

			try {
				console.log("Checking auth status...");
				authController.setCheckingAuth(true);

				const response = await apiClient.get<AuthApiResponse>(apiEndpoints.auth.me, "Auth check failed");

				// Fix the data access path
				const userData = response?.data?.user;

				// Update auth state with checked data
				if (userData) {
					authController.setAuthenticated(true, userData);
					return response;
				}

				// If we get here, we didn't get valid user data
				authController.resetAuth();
				return null;
			} catch (error) {
				console.error("Auth check failed:", error);
				authController.resetAuth();
				throw error;
			} finally {
				authController.setCheckingAuth(false);
			}
		},
		// Don't automatically retry auth checks
		retry: false,
		// Don't refetch on window focus - we'll handle this manually
		refetchOnWindowFocus: false,
	});

	// Create debounced version of refetchAuth to prevent excessive calls during navigation
	const debouncedRefetchAuth = useCallback(
		debounce(() => {
			if (!state.isAuthenticated && state.isInitialized && !state.isCheckingAuth && !state.isRefreshing) {
				console.log("Debounced auth check triggered");
				refetchAuth();
			}
		}, 300),
		[refetchAuth, state]
	);

	// Login mutation
	const loginMutation = useMutation({
		mutationFn: async (data: LoginInput) => {
			return apiClient.post<AuthApiResponse>(apiEndpoints.auth.login, data, "Failed to login");
		},
		onSuccess: (response) => {
			console.log("Login response structure:", JSON.stringify(response, null, 2));

			// Correct path to access user data
			const userData = response.data.user;

			// Check if we got the expected data structure
			if (!userData) {
				console.error("Unexpected response structure:", response);
				throw new Error("Invalid response format: missing user data");
			}

			console.log("Login successful, updating auth state");
			authController.setAuthenticated(true, userData);
			authController.debugState();

			// Get return path from URL
			const returnPath = getReturnPath(window.location.search);
			console.log("Redirecting to:", returnPath);
			navigate(returnPath);

			notifications.show({
				title: "Success",
				message: "Successfully logged in",
				color: "green",
			});
		},
		onError: (error: Error) => {
			console.error("useAuth (mutation onError):", error);
		},
	});

	const logout = useCallback(async () => {
		try {
			await apiClient.post(apiEndpoints.auth.logout, {}, "Logout failed");
			authController.resetAuth();

			// Navigate to login page after logout - without return path
			navigateToLogin(navigate, undefined, true);

			notifications.show({
				title: "Success",
				message: "Successfully logged out",
				color: "green",
			});
		} catch (error) {
			console.error("Logout failed:", error);
			notifications.show({
				title: "Error",
				message: "Failed to logout",
				color: "red",
			});

			// Even if logout fails, still try to navigate to login - without return path
			navigateToLogin(navigate, undefined, true);
		}
	}, [navigate]);

	// Add this useEffect to check auth on mount - using the debounced version to avoid race conditions
	useEffect(() => {
		// Only check if we're initialized but not authenticated
		if (state.isInitialized && !state.isAuthenticated) {
			console.log("Checking authentication on component mount");
			debouncedRefetchAuth();
		}
	}, [state.isInitialized, state.isAuthenticated, debouncedRefetchAuth]);

	return {
		...state,
		// Login function with improved error handling
		login: async (data: LoginInput): Promise<{ success: boolean; error?: string }> => {
			try {
				console.log("Login API call starting with email:", data.email);
				// Call the API directly
				const response = await apiClient.post<AuthApiResponse>(
					apiEndpoints.auth.login,
					data,
					"Failed to login"
				);

				console.log("Login API response received:", response);

				// Check if the response has the expected success structure
				if (response && response.success === true && response.data?.user) {
					console.log("Successful login detected, user data:", response.data.user);
					// Update auth state
					authController.setAuthenticated(true, response.data.user);

					// Navigate to the return path or dashboard
					const returnPath = getReturnPath(window.location.search);
					console.log("Redirecting to:", returnPath);
					navigate(returnPath);

					return { success: true };
				} else {
					console.error("Invalid API response format:", response);
					return {
						success: false,
						error: "Invalid response from server",
					};
				}
			} catch (error: any) {
				console.error("Login error caught with full details:", error);

				// Extract error message from various possible formats
				let errorMessage = "Authentication failed";

				try {
					// Check for API error response
					if (error.response && typeof error.response === "object") {
						console.log("Error has response object:", error.response);
						if (error.response.error?.message) {
							errorMessage = error.response.error.message;
							console.log("Extracted error message:", errorMessage);
						}
					} else if (error.message) {
						errorMessage = error.message;
						console.log("Using error.message:", errorMessage);
					}
				} catch (e) {
					// If error extraction fails, use default message
					console.error("Error parsing error details:", e);
				}

				return {
					success: false,
					error: errorMessage,
				};
			}
		},
		isLoggingIn: loginMutation.isPending,
		logout,
		refresh,
		refetchAuth: debouncedRefetchAuth, // Use debounced version for external calls
	};
};
